import { Injectable, inject } from '@angular/core';
import type { Observable } from 'rxjs';
import { shareReplay } from 'rxjs';

import type {
    SessionDetail,
    SessionItem,
    SpanDetail,
    TraceDetail,
    TraceFacets,
    TraceFilterParams,
    TraceItem,
    TraceSort,
    TraceSummary,
    UnpricedModels,
} from '@app/modules/monitor/models/trace.model';
import type {
    QueryFields,
    SpanSearchPage,
    SpanSort,
    Timeseries,
} from '@app/modules/monitor/models/trace-explore.model';
import { TRACE_PATH } from '@app/shared/constants/api-route.config';
import type { ItemResponse, ListResponse } from '@app/shared/services/mixins/crud.service';
import { RestService } from '@app/shared/services/rest.service';

/** The page size the list asks for. The API caps a page at 500 and reports `pageInfo.hardLimit`. */
export const TRACE_PAGE_SIZE = 50;

/** The number of facet values the API returns before it sets `truncated`. */
export const TRACE_FACET_LIMIT = 50;

/** `pageInfo` as the trace list serves it: a total, plus the cap when the caller over-asked. */
export type TracePageResponse = ListResponse<TraceItem> & {
    pageInfo?: { total: number; hardLimit?: number };
};

@Injectable({ providedIn: 'root' })
export class TraceService {
    private readonly api = inject(RestService);
    private readonly endpoint = TRACE_PATH;

    /**
     * One page of traces.
     *
     * Paging is stable: every sort the API offers ends on the unique invocation id, so a row cannot
     * appear on two pages or be skipped between them.
     * @param filters The window and the field filters, shared with facets and summary.
     * @param sort One of the four orders the API offers.
     * @param skip How many rows to step over. The API caps this at 100 000.
     * @param take The page size. The API caps this at 500.
     * @returns The page and its `pageInfo`.
     */
    list(filters: TraceFilterParams, sort: TraceSort, skip: number, take = TRACE_PAGE_SIZE) {
        return this.api.getWithParams<TracePageResponse>(this.endpoint, {
            ...filters,
            sort,
            skip,
            take,
        });
    }

    /**
     * Value counts for the checkbox filters. Each facet ignores its own filter.
     * @param filters The same filter block the list sends, so the counts match the list.
     * @param limit How many values per facet before the API sets `truncated`.
     * @returns One `FacetValues` per counted field.
     */
    /**
     * One page of sessions, most recently active first.
     *
     * A session groups the traces that share a `sessionId`. The same filters the list takes narrow
     * this the same way, so a window or a project applies to both views.
     * @param filters The window and the field filters.
     * @param skip How many sessions to step over.
     * @param take The page size. The API caps this at 500.
     * @returns The page and its `pageInfo`.
     */
    sessions(filters: TraceFilterParams, skip = 0, take = TRACE_PAGE_SIZE, flaggedOnly = false) {
        return this.api.getWithParams<
            ListResponse<SessionItem> & {
                pageInfo?: { total: number; spanMatchComplete?: boolean };
            }
        >(
            `${this.endpoint}sessions`,
            // `flagged` narrows which sessions are listed, never what is counted inside one.
            { ...filters, skip, take, flagged: flaggedOnly ? true : null },
        );
    }

    /**
     * One session as a transcript: its rollup and its turns, oldest first.
     *
     * No window is sent: the API defaults this read to the whole retention period, because a
     * reader arrives with a session id rather than a date. `showError` is false so a 404 is the
     * page's own not-found state rather than a toast.
     * @param sessionId The session, as the trace headers carry it.
     * @returns The rollup and the turns.
     */
    session(sessionId: string) {
        return this.api.get<ItemResponse<SessionDetail>>(
            `${this.endpoint}sessions/${encodeURIComponent(sessionId)}`,
            false,
        );
    }

    facets(filters: TraceFilterParams, limit = TRACE_FACET_LIMIT) {
        return this.api.getWithParams<ItemResponse<TraceFacets>>(`${this.endpoint}facets`, {
            ...filters,
            limit,
        });
    }

    /**
     * Aggregates for the KPI strip.
     *
     * `showError` is false so a failure hides the strip instead of raising a toast. The list fires
     * against the same filters at the same moment, and two toasts for one bad filter is noise.
     * @param filters The same filter block the list sends.
     * @returns The window's totals, including the cost states that have no total at all.
     */
    summary(filters: TraceFilterParams) {
        return this.api.getWithParams<ItemResponse<TraceSummary>>(
            `${this.endpoint}summary`,
            { ...filters },
            false,
        );
    }

    /**
     * The trace header and one page of its span tree. Span bodies are fetched one at a time.
     *
     * A trace can hold more spans than one response should carry, so the tree comes back a page at
     * a time: read `spans`, and while `nextSpanId` is not null ask again with that value as
     * `afterSpanId`. An unknown cursor is a 404 and a malformed one a 400.
     * @param traceId The 32 hex characters of the trace.
     * @param afterSpanId The last span already held, or null for the first page.
     * @returns The header, this page of spans, and the cursor for the next page.
     */
    get(traceId: string, afterSpanId: string | null = null) {
        return this.api.getWithParams<ItemResponse<TraceDetail>>(`${this.endpoint}${traceId}`, {
            afterSpanId,
        });
    }

    /**
     * One span in full: input, output, attributes and what was dropped from them.
     *
     * `showError` is false. This fires from the tree selection, which the arrow keys move a row at
     * a time, so a toast per failure is a storm of them for one bad moment on the network — and
     * the inspector already puts the failure inline, in the panel the reader is looking at.
     * @param traceId The trace the span belongs to.
     * @param spanId The 16 hex characters of the span.
     * @returns The span with its bodies attached.
     */
    getSpan(traceId: string, spanId: string) {
        return this.api.get<ItemResponse<SpanDetail>>(
            `${this.endpoint}${traceId}/spans/${spanId}`,
            false,
        );
    }

    /**
     * Models whose spans could not be priced, most spans first.
     *
     * The answer is an object, not a list: the scan is bounded at 500 000 rows and says so through
     * `truncated`, which makes every `spanCount` a lower bound and can leave a model out of
     * `models` altogether. Read it through `unpricedModelsNote` rather than rendering the rows
     * as if they were the whole picture.
     * @param projectId Narrows the scan to one project; null counts every readable project.
     * @param days The whole UTC days to scan, both inclusive — the list's window, from
     *   `scanDaysFor`, so the banner speaks for the totals it sits above. At most 90 days.
     * @returns The provider/model pairs with no price, how many spans each left unpriced, and
     *   whether the scan reached the end.
     */
    /**
     * One page of the span search: the spans matching a span query among the traces the list's
     * filters keep.
     *
     * `showError` is false: a bad query is answered inline under the query box, and a toast for
     * the same 400 would say it twice.
     * @param filters The list's filters; their `query` is ignored (the span query goes separately).
     * @param query The span query, or null for every span.
     * @param sort One of `SPAN_SORTS`.
     * @param skip How many matches to step over. The API caps this at 10 000.
     * @param take The page size. The API caps this at 200.
     * @returns The page, `hasMore`, and whether the scan saw the whole window.
     */
    spans(
        filters: TraceFilterParams,
        query: string | null,
        sort: SpanSort,
        skip: number,
        take = TRACE_PAGE_SIZE,
    ) {
        return this.api.getWithParams<ItemResponse<SpanSearchPage>>(
            `${this.endpoint}spans`,
            { ...filters, query, sort, skip, take },
            false,
        );
    }

    private queryFieldsRequest: Observable<ItemResponse<QueryFields>> | null = null;

    /**
     * Every field the query language takes, for the help popover and autocomplete. Asked for once
     * and shared: the catalog is the server's code, not data.
     * @returns Both catalogs.
     */
    queryFields(): Observable<ItemResponse<QueryFields>> {
        this.queryFieldsRequest ??= this.api
            .get<ItemResponse<QueryFields>>(`${this.endpoint}query-fields`, false)
            .pipe(shareReplay({ bufferSize: 1, refCount: false }));

        return this.queryFieldsRequest;
    }

    /**
     * Monitor-catalog metrics per time bucket over the list's filters.
     * @param filters The list's filters and query.
     * @param metrics Metric keys, as monitors name them.
     * @param options Grouping, bucket size, and the params a metric takes.
     * @returns The buckets, the groups and one series per metric, group and currency.
     */
    timeseries(
        filters: TraceFilterParams,
        metrics: string[],
        options: {
            groupBy?: string | null;
            bucketSeconds?: number | null;
            currency?: string | null;
        } = {},
    ) {
        return this.api.getWithParams<ItemResponse<Timeseries>>(
            `${this.endpoint}timeseries`,
            { ...filters, metric: metrics, ...options },
            false,
        );
    }

    /**
     * The trace list or the span search as a file. The API writes at most 50 000 rows and records
     * the export in its audit log.
     * @param kind `traces` for the list, `spans` for the span search.
     * @param format `csv` or `jsonl`.
     * @param params The filters, query and sort the page is showing.
     * @returns The response, whose body is the file.
     */
    export(kind: 'traces' | 'spans', format: 'csv' | 'jsonl', params: Record<string, unknown>) {
        const cleaned = Object.fromEntries(
            Object.entries({ ...params, format }).filter(
                ([, value]) => value !== null && value !== undefined,
            ),
        );

        // No toast: the page says why an export failed, in words read out of the Blob body.
        return this.api.exportUsingGet(
            kind === 'traces' ? `${this.endpoint}export` : `${this.endpoint}spans/export`,
            cleaned,
            false,
        );
    }

    unpricedModels(projectId: number | null, days: { startDate: string; endDate: string }) {
        return this.api.getWithParams<ItemResponse<UnpricedModels>>(
            `${this.endpoint}unpriced-models`,
            { projectId, ...days },
            false,
        );
    }
}
